import { computed, ref } from 'vue'
import useContentPacks from '@/composables/api/contentPacks'
import useAcademies from '@/composables/api/academies'
import useSnackbar, { SnackbarType } from '@/composables/snackbar'

const CONTENT_PACK_ACTIONS = ['content-pack-add', 'content-pack-delete']
const ACADEMY_ACTIONS = ['academy-include', 'academy-exclude']

const normalizeContentPackReference = reference => {
  if (reference && typeof reference === 'object') {
    return {
      id: reference.id,
      academyId: reference.academyId ?? null,
    }
  }

  return {
    id: reference,
    academyId: null,
  }
}

const stripHtml = value => String(value || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()

const isContentPackAcademy = value => String(value || '').trim().toUpperCase() === 'CONTENTPACK'
const isGlobalOrContentPackAcademy = value => ['GLOBAL', 'CONTENTPACK', 'GLOBAL CONTENTPACK', 'GLOBAL & CONTENTPACK']
  .includes(stripHtml(value).toUpperCase())

const hasAssignedAcademy = item => {
  if (item.academyId !== null && item.academyId !== undefined && item.academyId !== '' && !isContentPackAcademy(item.academyId)) {
    return true
  }

  if (item.companyId !== null && item.companyId !== undefined && item.companyId !== '' && !isContentPackAcademy(item.companyId)) {
    return true
  }

  const academyLabel = typeof item.academy === 'object'
    ? stripHtml(item.academy?.name || item.academy?.displayName || item.academy?.title)
    : stripHtml(item.academy)
  if (academyLabel && academyLabel !== '---' && !isGlobalOrContentPackAcademy(academyLabel)) {
    return true
  }

  const academyName = stripHtml(item.academyName)

  return Boolean(academyName && !isGlobalOrContentPackAcademy(academyName))
}

export default function useContentPackBulkActions({
  selectedItems,
  sectionKey,
  subsectionKey,
  libraryType,
  updateManyItems,
}) {
  const {
    contentPacks,
    fetchContentPacks,
    updateContentPack,
  } = useContentPacks()
  const {
    academies,
    fetchAcademies,
  } = useAcademies()
  const { showSnackbar } = useSnackbar()

  const isContentPackDialogVisible = ref(false)
  const isContentPackConfirmationDialogVisible = ref(false)
  const isAcademyDialogVisible = ref(false)
  const isAcademyConfirmationDialogVisible = ref(false)
  const selectedContentPackId = ref(null)
  const selectedAcademyId = ref(null)
  const pendingAction = ref(null)

  const selectedContentPack = computed(() => (
    contentPacks.value.find(item => item.id === selectedContentPackId.value) || null
  ))
  const selectedAcademy = computed(() => (
    academies.value.find(item => item.id === selectedAcademyId.value) || null
  ))

  const contentPackOptions = computed(() => contentPacks.value.map(item => ({
    text: item.displayName,
    value: item.id,
  })))
  const academyOptions = computed(() => academies.value.map(item => ({
    text: item.name,
    value: item.id,
  })))

  const contentPackConfirmationTitle = computed(() => (
    pendingAction.value === 'content-pack-add'
      ? 'Assign to Content Pack'
      : 'Remove Items from Content Pack'
  ))

  const contentPackConfirmationMessage = computed(() => {
    const displayName = selectedContentPack.value?.displayName || ''

    if (pendingAction.value === 'content-pack-add') {
      return `Are you sure you want to assign these items to the content pack "${displayName}"?`
    }

    return `Are you sure you want to remove these items from the content pack "${displayName}"?`
  })
  const academyDialogTitle = computed(() => (
    pendingAction.value === 'academy-include'
      ? 'Include in Academy'
      : 'Exclude from Academy'
  ))
  const academyConfirmationTitle = computed(() => academyDialogTitle.value)
  const academyConfirmationMessage = computed(() => {
    const academyName = selectedAcademy.value?.name || ''

    if (pendingAction.value === 'academy-include') {
      return `Are you sure you want to include these items in academy "${academyName}"?`
    }

    return `Are you sure you want to exclude these items from academy "${academyName}" and set companyId to null?`
  })

  const getSelectedContentPackNames = selectedIds => {
    const selectedIdSet = new Set(selectedIds)

    return contentPacks.value
      .filter(contentPack => {
        const references = (((contentPack || {})[sectionKey] || {})[subsectionKey] || [])
          .map(reference => normalizeContentPackReference(reference))

        return references.some(reference => selectedIdSet.has(reference.id))
      })
      .map(contentPack => contentPack.displayName)
  }

  const validateAssignToContentPack = () => {
    const invalidItems = selectedItems.value.filter(item => hasAssignedAcademy(item))

    if (invalidItems.length === 0) return true

    const academyNames = Array.from(new Set(invalidItems
      .map(item => {
        if (typeof item.academy === 'object') {
          return stripHtml(item.academy?.name || item.academy?.displayName || item.academy?.title)
            || stripHtml(item.academyName)
            || item.companyId
            || item.academyId
        }

        return stripHtml(item.academy) || stripHtml(item.academyName) || item.companyId || item.academyId
      })
      .filter(Boolean)))

    showSnackbar(
      `The selected items cannot be saved because the list contains items belonging to academies: ${academyNames.join(', ')}. Remove them before saving.`,
      SnackbarType.ERROR,
    )

    return false
  }

  const validateAcademyAction = async () => {
    await fetchContentPacks()

    const contentPackNames = Array.from(new Set(getSelectedContentPackNames(
      selectedItems.value.map(item => item.id),
    )))

    if (contentPackNames.length === 0) return true

    showSnackbar(
      `The operation cannot be performed on the selected items because the list contains items belonging to Content Packs: ${contentPackNames.join(', ')}. Remove them from the Content Pack before saving.`,
      SnackbarType.ERROR,
    )

    return false
  }

  const resetContentPackDialogs = () => {
    isContentPackDialogVisible.value = false
    isContentPackConfirmationDialogVisible.value = false
    isAcademyDialogVisible.value = false
    isAcademyConfirmationDialogVisible.value = false
    selectedContentPackId.value = null
    selectedAcademyId.value = null
    pendingAction.value = null
  }

  const hideContentPackDialog = () => {
    isContentPackDialogVisible.value = false
    selectedContentPackId.value = null
    pendingAction.value = null
  }

  const showContentPackConfirmationDialog = () => {
    isContentPackDialogVisible.value = false
    isContentPackConfirmationDialogVisible.value = true
  }

  const hideAcademyDialog = () => {
    isAcademyDialogVisible.value = false
    selectedAcademyId.value = null
    pendingAction.value = null
  }

  const showAcademyConfirmationDialog = () => {
    isAcademyDialogVisible.value = false
    isAcademyConfirmationDialogVisible.value = true
  }

  const beginBulkAction = async action => {
    if (CONTENT_PACK_ACTIONS.includes(action)) {
      pendingAction.value = action
      await fetchContentPacks()
      isContentPackDialogVisible.value = true

      return true
    }

    if (!ACADEMY_ACTIONS.includes(action)) return false

    pendingAction.value = action
    await fetchAcademies()
    isAcademyDialogVisible.value = true

    return true
  }

  const applyContentPackAction = async () => {
    if (!selectedContentPack.value || !pendingAction.value) return false
    if (pendingAction.value === 'content-pack-add' && !validateAssignToContentPack()) return false

    const updatedPack = structuredClone(selectedContentPack.value)
    const selectedIds = selectedItems.value.map(item => item.id)

    updatedPack[sectionKey] = updatedPack[sectionKey] || {
      exercises: [],
      sessions: [],
      assessments: [],
    }

    const currentReferences = (updatedPack[sectionKey][subsectionKey] || [])
      .map(reference => normalizeContentPackReference(reference))

    if (pendingAction.value === 'content-pack-add') {
      const referencesById = new Map(currentReferences.map(reference => [reference.id, reference]))

      selectedIds.forEach(id => {
        if (!referencesById.has(id)) {
          referencesById.set(id, { id, academyId: null })
        }
      })

      updatedPack[sectionKey][subsectionKey] = Array.from(referencesById.values())
    } else {
      updatedPack[sectionKey][subsectionKey] = currentReferences
        .filter(reference => !selectedIds.includes(reference.id))
    }

    await updateContentPack(updatedPack)
    await fetchContentPacks()
    resetContentPackDialogs()

    return true
  }

  const applyAcademyAction = async () => {
    if (!pendingAction.value || !updateManyItems || !libraryType || !selectedAcademy.value) return false
    if (pendingAction.value === 'academy-include' && !(await validateAcademyAction())) return false

    const selectedIds = selectedItems.value.map(item => item.id)
    const academyId = pendingAction.value === 'academy-include' ? selectedAcademyId.value : null

    await updateManyItems(libraryType, 'companyId', academyId, selectedIds)
    resetContentPackDialogs()

    return true
  }

  return {
    isContentPackDialogVisible,
    isContentPackConfirmationDialogVisible,
    isAcademyDialogVisible,
    isAcademyConfirmationDialogVisible,
    selectedContentPackId,
    selectedAcademyId,
    contentPackOptions,
    academyOptions,
    contentPackConfirmationTitle,
    contentPackConfirmationMessage,
    academyDialogTitle,
    academyConfirmationTitle,
    academyConfirmationMessage,
    hideContentPackDialog,
    hideAcademyDialog,
    showContentPackConfirmationDialog,
    showAcademyConfirmationDialog,
    beginBulkAction,
    applyContentPackAction,
    applyAcademyAction,
    resetContentPackDialogs,
  }
}
